import { FC, useEffect, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { initialPaymentState } from '../../components/payment-method-modal/AddPaymentMethodModal';
import { AlertSVG } from '../../components/svgs';
import { Loader } from '../../components';
import PaymentDetails from '../../components/payment-method-modal/PaymentDetails';
import BillingAddress from '../../components/payment-method-modal/BillingAddress';
import { RequireAlert } from '../inventory-management/InventoryManagementCollectDeposit';
import { ShippingSummary } from '../../components/insurance-form/InsuranceForm';
import { BulkTransaction } from '../../types/interfaces';
import { AccountRoles, AppRoles, PaymentActions, PaymentMethods, ShippingPaymentMethods } from '../../types/enums';
import { PaymentMethod } from '../../types/interfaces/paymentMethod.interface';
import { getSubscriptionStatus } from '../../api/paymentGatewayApi';
import { makeInstantShippingPayment, makeShippingPaymentUsingCardOnFile } from '../../api/shippingPaymentApi';
import { addShippingPaymentTransactions, updateShippingPaymentTransactions } from '../../api/shippingPaymentTransactionApi';
import { getPaymentDetails, mapPaymentInfo } from '../../services/payment.service';
import { showToast } from '../../services/toast.service';
import { PermissionService } from '../../services/permission.service';
import { dollarsToCents } from '../../services/account.service';
import { toastMessages } from '../../constants/errorMessages';
import { BUTTON_CONSTANTS, LABEL_CONSTANTS, NOTIFICATION_CONSTANTS, PAYMENT_CONSTANTS } from '../../constants/common';

interface MergeShippingPaymentInfoProps {
  setIsPaymentPage: (isPreview: boolean) => void;
  shippingSummary: ShippingSummary | undefined;
  totalShippingAmount: number;
  bulkTransaction: BulkTransaction | undefined;
  handleMergeOrder: () => void;
}

const MergeShippingPaymentInfo: FC<MergeShippingPaymentInfoProps> = ({
  setIsPaymentPage,
  shippingSummary,
  totalShippingAmount,
  bulkTransaction,
  handleMergeOrder,
}) => {
  const accountRoles = useAppSelector(state => state.auth.accountContext.roles) as AccountRoles[];
  const appRoles = useAppSelector(state => state.auth.appContext.roles) as AppRoles[];
  const enabled = useAppSelector(state => state.user.enabled);
  const verified = useAppSelector(state => state.user.verified);

  const permissionService = new PermissionService(accountRoles, appRoles, enabled, verified);
  const initialFormState = { value: '', valid: false };

  const [paymentInfo, setPaymentInfo] = useState<PaymentMethod>({ ...initialPaymentState, paymentType: initialFormState });
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>({ ...initialPaymentState, paymentType: initialFormState });
  const [shippingPaymentMethod, setShippingPaymentMethod] = useState<string>('');
  const [validate, setValidate] = useState<boolean>(false);
  const [placingOrder, setPlacingOrder] = useState<boolean>(true);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [notes, setNotes] = useState<string>('');
  const [makingPayment, setMakingPayment] = useState<boolean>(true);

  useEffect(() => {
    const fetchPaymentDetails = async () => {
      setIsLoading(true);
      try {
        const { data: paymentStatus } = await getSubscriptionStatus(bulkTransaction?.accountId!);

        if (bulkTransaction?.accountId! && paymentStatus) {
          const isUserAdmin = permissionService.userHasRSGAdminPermission();
          const paymentData = await getPaymentDetails(bulkTransaction?.accountId!, isUserAdmin);
          paymentData && setPaymentInfo(paymentData);
        }
      } catch (error) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPaymentDetails();
  }, [bulkTransaction]);

  useEffect(() => {
    paymentInfo != undefined && setPaymentMethod(paymentInfo);
    if (paymentInfo?.paymentType?.value === PaymentMethods.CARD) {
      setShippingPaymentMethod(ShippingPaymentMethods.CARD_ON_FILE);
    } else {
      setShippingPaymentMethod(ShippingPaymentMethods.NEW_CARD);
      setPaymentMethod(initialPaymentState);
    }
  }, [paymentInfo]);

  useEffect(() => {
    if (shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE && paymentInfo) {
      setPaymentMethod(paymentInfo);
    } else if (shippingPaymentMethod === ShippingPaymentMethods.NEW_CARD) {
      setPaymentMethod(initialPaymentState);
    }
  }, [shippingPaymentMethod]);

  const checkValidity = () => {
    const { paymentType, name, address, city, region, postalCode, country, cardNumber, expiry, cvv } = paymentMethod || {};

    const isCardPayment = paymentMethod?.paymentType?.value === PaymentMethods.CARD;
    const isCardOnFile = shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE;
    const isMarkAsPaid = shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID;
    const isBillingAddressValid = address?.valid && city?.valid && region?.valid && postalCode?.valid && country?.valid;
    const isCardValid = cardNumber?.valid && expiry?.valid && (isCardOnFile || cvv?.valid);
    const hasNotes = notes.trim().length > 0;

    return (
      (paymentType?.valid && name?.valid && isBillingAddressValid && isCardPayment && isCardValid) || (isMarkAsPaid && hasNotes)
    );
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!placingOrder) return;

    setPlacingOrder(false);
    setValidate(true);

    const paymentInfo = mapPaymentInfo(paymentMethod);

    if (bulkTransaction?.accountId && checkValidity()) {
      try {
        let paymentResponse = null;

        const commonPayload = {
          shippingOutBoundCostCents: dollarsToCents(shippingSummary?.shippingOutBoundCost || 0),
          shippingReturnCostCents: dollarsToCents(shippingSummary?.shippingReturnCost || 0),
          tankRentalFeeCents: dollarsToCents(shippingSummary?.tankRentalFee || 0),
          totalAmountPaidCents: dollarsToCents(totalShippingAmount),
          insuredAmountCents: dollarsToCents(Number(shippingSummary?.insuranceAmount.value) || 0),
          insurancePremiumCents: dollarsToCents(shippingSummary?.insurance || 0),
          processingFeeCents: dollarsToCents(shippingSummary?.handlingProcessingFee || 0),
        };

        const isCardOnFile = shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE;
        const isNewCard = shippingPaymentMethod === ShippingPaymentMethods.NEW_CARD;
        const isMarkAsPaid = shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID;

        switch (shippingPaymentMethod) {
          case ShippingPaymentMethods.CARD_ON_FILE:
            paymentResponse = await handlePaymentUsingCardOnFile(commonPayload);
            break;

          case ShippingPaymentMethods.NEW_CARD:
            paymentResponse = await handleInstantPayment({ ...paymentInfo, ...commonPayload });
            break;
        }

        if ((isCardOnFile || isNewCard) && paymentResponse?.data?.transactionDetails?.status === 'A') {
          const shippingPaymentId = paymentResponse.data.shippingPaymentId;
          if (bulkTransaction?.bulkOrderId && shippingPaymentId) {
            const payload = { bulkOrderId: bulkTransaction?.bulkOrderId };
            await updateShippingPaymentTransactions(shippingPaymentId, payload);
            if (handleMergeOrder) {
              handleMergeOrder();
            }
          }
        } else if (isMarkAsPaid) {
          if (bulkTransaction?.bulkOrderId) {
            const payload = {
              ...commonPayload,
              bulkOrderId: bulkTransaction?.bulkOrderId,
              accountId: bulkTransaction?.accountId,
              notes: notes,
            };

            await addShippingPaymentTransactions(payload);
            if (handleMergeOrder) {
              handleMergeOrder();
            }
          }
        }
      } catch (error: any) {
        showToast.error(toastMessages.SOMETHING_WENT_WRONG);
        setValidate(false);
      } finally {
        setPlacingOrder(true);
      }
    } else {
      setPlacingOrder(true);
    }
  };

  const handlePaymentUsingCardOnFile = async (commonPayload: any) => {
    if (!makingPayment) return;
    setMakingPayment(false);

    try {
      const response = await makeShippingPaymentUsingCardOnFile(bulkTransaction?.accountId!, commonPayload);
      handlePaymentResponse(response);
      return response;
    } catch {
      showToast.error(toastMessages.PAYMENT_FAIL);
      setValidate(false);
      return null;
    } finally {
      setMakingPayment(true);
    }
  };

  const handleInstantPayment = async (paymentInfo?: any) => {
    if (!makingPayment) return;
    setMakingPayment(false);

    try {
      const response = await makeInstantShippingPayment(bulkTransaction?.accountId!, paymentInfo);
      handlePaymentResponse(response);
      return response;
    } catch (error: any) {
      if (error?.response?.status === 701) {
        showToast.error(error.response?.data?.title);
      } else {
        showToast.error(toastMessages.PAYMENT_FAIL);
      }
      setValidate(false);
      return null;
    } finally {
      setMakingPayment(true);
    }
  };

  const handlePaymentResponse = (response: any) => {
    if (response?.data?.transactionDetails?.status === 'A') {
      showToast.success(toastMessages.PAYMENT_SUCCESS);
    } else {
      showToast.error(toastMessages.PAYMENT_FAIL);
    }
  };

  return (
    <div className="shipping-payment-information add-payment-method">
      <div className="payment-info-header">
        <h2>{PAYMENT_CONSTANTS.PAYMENT_INFORMATION}</h2>
      </div>
      {paymentInfo?.paymentType?.value === PaymentMethods.ACH && (
        <div className="payment-info-subheader ach-warning">
          <AlertSVG />
          <label>{NOTIFICATION_CONSTANTS.ACH_PAYMENT_NOT_ALLOWED}</label>
        </div>
      )}
      {isLoading ? (
        <Loader loaderSize="small" addedSpace />
      ) : (
        <form action="submit" onSubmit={(event: React.FormEvent) => handleSubmit(event)}>
          <div className="payment-method-selection">
            {paymentInfo?.paymentType?.value != PaymentMethods.ACH && <label>{LABEL_CONSTANTS.CHOOSE_PAYMENT_MODE}</label>}
            <>
              <div className="radio-row">
                {paymentInfo?.paymentType?.value === PaymentMethods.CARD && (
                  <>
                    <input
                      type="radio"
                      id="payment-method-card-on-file"
                      name="payment-method-card-on-file"
                      value={shippingPaymentMethod}
                      onChange={() => setShippingPaymentMethod(ShippingPaymentMethods.CARD_ON_FILE)}
                      checked={shippingPaymentMethod === ShippingPaymentMethods.CARD_ON_FILE}
                    />
                    <label htmlFor="payment-method-card-on-file">{LABEL_CONSTANTS.USE_CARD_ON_FILE}</label>
                  </>
                )}
                {paymentInfo?.paymentType?.value === PaymentMethods.ACH && !permissionService.userHasRSGAdminPermission() ? (
                  <></>
                ) : (
                  <>
                    <input
                      type="radio"
                      id="payment-method-new-card"
                      name="payment-method-new-card"
                      value={shippingPaymentMethod}
                      onChange={() => setShippingPaymentMethod(ShippingPaymentMethods.NEW_CARD)}
                      checked={shippingPaymentMethod === ShippingPaymentMethods.NEW_CARD}
                    />
                    <label htmlFor="payment-method-new-card">{LABEL_CONSTANTS.ENTER_NEW_CARD_DETAILS}</label>
                  </>
                )}
                {permissionService.userHasRSGAdminPermission() && (
                  <>
                    <input
                      type="radio"
                      id="payment-method-mark-as-paid"
                      name="payment-method-mark-as-paid"
                      value={shippingPaymentMethod}
                      onChange={() => setShippingPaymentMethod(ShippingPaymentMethods.MARK_AS_PAID)}
                      checked={shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID}
                    />
                    <label htmlFor="payment-method-mark-as-paid">{LABEL_CONSTANTS.MARK_AS_PAID}</label>
                  </>
                )}
              </div>
            </>
          </div>

          {(!placingOrder || !makingPayment) && <Loader loaderSize={'medium'} />}

          {shippingPaymentMethod === ShippingPaymentMethods.MARK_AS_PAID ? (
            <div className="mark-as-paid">
              <label className="mark-as-paid-question">{PAYMENT_CONSTANTS.CONFIRM_SKIP_PAYMENT_MESSAGE}</label>
              <div className="mark-as-paid-info">
                <p>By selecting this option:</p>
                <ul>
                  <li>{PAYMENT_CONSTANTS.SKIP_PAYMENT_NO_CARD_REQUIRED}</li>
                  <li>{PAYMENT_CONSTANTS.SKIP_PAYMENT_ASSUMED_COMPLETED}</li>
                  <li>{PAYMENT_CONSTANTS.SKIP_PAYMENT_NO_CHARGES_PROCESSED}</li>
                  <li>{PAYMENT_CONSTANTS.NO_INVOICE_PAYMENT_RECORD_IN_QB}</li>
                </ul>
              </div>
              <div className="form-row expanded-text-area">
                <label>
                  *{LABEL_CONSTANTS.NOTES}:
                  <RequireAlert isActive={validate && !notes} />
                </label>
                <textarea value={notes} placeholder="Notes" onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
          ) : (
            <>
              <PaymentDetails
                paymentMethod={paymentMethod}
                validate={validate}
                buttonAction={PaymentActions.SHIPPING_PAYMENT}
                paymentMethodType={shippingPaymentMethod}
                setPaymentMethod={setPaymentMethod}
              />
              <BillingAddress
                paymentMethod={paymentMethod}
                validate={validate}
                buttonAction={PaymentActions.SHIPPING_PAYMENT}
                paymentMethodType={shippingPaymentMethod}
                setPaymentMethod={setPaymentMethod}
              />
              <div className="payment-transaction-note">
                {paymentMethod?.paymentType?.value === PaymentMethods.CARD && (
                  <div className="transaction-alert">
                    <AlertSVG />
                    <p>{NOTIFICATION_CONSTANTS.CC_PROCESSING_FEE_MSG}</p>
                  </div>
                )}
              </div>
            </>
          )}
          <div className="buttons">
            <button type="button" className="button red inverted medium" onClick={() => setIsPaymentPage(false)}>
              {BUTTON_CONSTANTS.CANCEL}
            </button>
            <button type="submit" className={`button green medium ${checkValidity() ? '' : 'disabled'}`}>
              {BUTTON_CONSTANTS.SUBMIT}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
export default MergeShippingPaymentInfo;
